import * as React from "react";
import { cn } from "@ozpanel/ui";

/**
 * Form primitives for HAND-WRITTEN screens.
 *
 * This is a different job from DataForm, and the distinction is the reason a fourth way to render
 * a field is not being added here. DataForm renders a SettingsSpec the SERVER supplies — the
 * module Settings interface describes its own fields and DataForm draws whatever it is given. It
 * is used in exactly one place, ModuleDetailScreen, and correctly so. Everything else — a webmail
 * exposure dialog, a rate-limit editor, a policy override — is a form somebody wrote by hand for
 * one screen, and for those there was NOTHING.
 *
 * The result was measured on 2026-09-01: 76 copies of the same input markup, seven field helpers
 * redefined locally across five files, and five hand-rolled add/remove list editors in
 * VhostOptionsForm alone. Four of those helpers and four of those list editors were written in a
 * single session, by the same author, solving the same problem each time — which is the clearest
 * possible evidence that the missing piece was a primitive and not more discipline.
 *
 * Every control here keeps the properties the screens had converged on by hand, because they were
 * right and they are what a form needs to be honest:
 *   - the HELP sits under the control it belongs to, not in a legend somewhere else;
 *   - a PLACEHOLDER carries the default, so "empty" reads as "the default applies" rather than
 *     "nothing";
 *   - a CONSEQUENCE can be attached to the field that causes it, so the cost of a value is next
 *     to where the value is typed rather than in a paragraph read after the mistake.
 */

// The base carries `w-full`. A width added on top MUST go through `cn` (tailwind-merge), never
// string concatenation: Tailwind v4 emits `.w-full` AFTER every numeric width in the stylesheet,
// so `inputClass + " w-32"` rendered 100% wide whatever the class string said. Measured 2026-11-05
// on the website Settings tab: NumberField's unit sat at the far edge of the row, and the rate
// limit's request count (w-full + min-w-[8rem] inside a w-24 box) overflowed its box onto the
// "requests per" words beside it.
const inputClass =
  "w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 text-[12.5px] text-fg outline-none focus:border-accent";
const monoInputClass = inputClass + " font-mono";
const labelClass = "block text-[12px] text-muted";
const helpClass = "mt-1 block text-[11.5px] leading-relaxed text-muted";

/** What a field says about itself: the label above, the help below, the cost of getting it wrong. */
interface FieldChrome {
  label: React.ReactNode;
  /** Explains what the field does. */
  help?: React.ReactNode;
  /**
   * The COST of this value — what it turns off, exposes, or makes irreversible. Rendered in the
   * warning tone directly under the control, because a consequence in a separate paragraph is one
   * an operator reads after the decision rather than before it.
   */
  consequence?: React.ReactNode;
}

function Chrome({ label, help, consequence, children }: FieldChrome & { children: React.ReactNode }) {
  return (
    <label className={labelClass}>
      <span>{label}</span>
      <span className="mt-1 block">{children}</span>
      {help && <span className={helpClass}>{help}</span>}
      {consequence && (
        <span className={helpClass + " text-warn"}>{consequence}</span>
      )}
    </label>
  );
}

export interface FieldProps extends FieldChrome {
  value: string;
  onChange: (v: string) => void;
  /** Shown when the box is empty. Use the DEFAULT the server would apply, not an example. */
  placeholder?: string;
  /** Fixed-width, for anything a machine reads back: paths, addresses, rates, patterns. */
  mono?: boolean;
  type?: "text" | "password" | "email" | "url";
  disabled?: boolean;
  /** What a password manager or the phone's keyboard should offer ("current-password", "one-time-code"…). */
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}

/** A labelled text input. */
export function Field({ value, onChange, placeholder, mono, type = "text", disabled, autoComplete, inputMode, ...chrome }: FieldProps) {
  return (
    <Chrome {...chrome}>
      <input
        type={type}
        autoComplete={autoComplete}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={mono ? monoInputClass : inputClass}
      />
    </Chrome>
  );
}

export interface TextAreaFieldProps extends FieldChrome {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Fixed-width, for anything a machine reads back: keys, certificates, configuration. */
  mono?: boolean;
  rows?: number;
  disabled?: boolean;
  /** Secrets pasted in (a private key): no spellcheck, no autofill — the browser keeps nothing. */
  secret?: boolean;
}

/** A labelled multi-line input — a PEM, a reason, a block of configuration. */
export function TextAreaField({ value, onChange, placeholder, mono, rows = 3, disabled, secret, ...chrome }: TextAreaFieldProps) {
  return (
    <Chrome {...chrome}>
      <textarea
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        spellCheck={secret ? false : undefined}
        autoComplete={secret ? "off" : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={mono ? monoInputClass : inputClass}
      />
    </Chrome>
  );
}

export interface NumberFieldProps extends FieldChrome {
  value: number | string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Rendered after the box: "messages", "seconds", "MB". */
  unit?: string;
  disabled?: boolean;
}

/**
 * A number, kept as a STRING in the caller's state.
 *
 * Deliberate: a numeric input that coerces as you type cannot represent "" — and "" is a real,
 * distinct value in almost every form here, meaning "inherit" or "the plan's default". Coercing
 * it to 0 is how an empty box silently becomes a hard limit of zero.
 */
export function NumberField({ value, onChange, placeholder, unit, disabled, ...chrome }: NumberFieldProps) {
  return (
    <Chrome {...chrome}>
      <span className="flex items-center gap-2">
        <input
          value={String(value ?? "")}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={cn(monoInputClass, "w-32 min-w-0")}
        />
        {unit && <span className="shrink-0 whitespace-nowrap text-[11.5px] text-muted">{unit}</span>}
      </span>
    </Chrome>
  );
}

export interface ToggleProps extends FieldChrome {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}

/**
 * A checkbox whose label is clickable, with its help underneath.
 *
 * The box sits in a slot exactly one label line tall and is centred in it, so it lines up with the
 * FIRST line of the label at any box size — 13px on a desk, 22px on a touch screen (tokens.css) —
 * instead of a hand-tuned margin that fits only one of them. The slot never shrinks, so a long
 * label wraps beside the box rather than squeezing it.
 */
export function Toggle({ checked, onChange, disabled, label, help, consequence }: ToggleProps) {
  return (
    <label className="flex items-start gap-2 py-1 text-[12.5px] leading-5 pointer-coarse:min-h-11">
      <span className="flex h-5 shrink-0 items-center">
        <input
          type="checkbox"
          className="m-0"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block">{label}</span>
        {help && <span className={helpClass}>{help}</span>}
        {consequence && <span className={helpClass + " text-warn"}>{consequence}</span>}
      </span>
    </label>
  );
}

export interface SelectFieldProps extends FieldChrome {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; help?: string }[];
  disabled?: boolean;
}

/**
 * A choice. `options` carries its own labels so a caller cannot render a value the server will
 * reject — the recurring alternative was a free-text field where a typo stops a service starting,
 * which is why SettingsField.OptionsFrom exists on the server side for the same reason.
 */
export function SelectField({ value, onChange, options, disabled, ...chrome }: SelectFieldProps) {
  const chosen = options.find((o) => o.value === value);
  return (
    <Chrome
      {...chrome}
      help={
        <>
          {chrome.help}
          {chosen?.help && <span className="block">{chosen.help}</span>}
        </>
      }
    >
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Chrome>
  );
}

export interface ListEditorProps<T> {
  label?: string;
  help?: React.ReactNode;
  items: T[];
  onChange: (items: T[]) => void;
  /** A fresh item when "add" is pressed. */
  blank: () => T;
  /** Renders one row's controls. `set` replaces this row. */
  row: (item: T, set: (next: T) => void, index: number) => React.ReactNode;
  addLabel: string;
  /** Shown when there is nothing — say what the ABSENCE means, not "no items". */
  emptyMessage?: string;
  /** Wraps each row in a bordered block, for rows that contain nested lists of their own. */
  boxed?: boolean;
}

/**
 * The add / edit / remove list.
 *
 * Written by hand five times in VhostOptionsForm alone — blocked paths, cache rules, redirects,
 * password-protected areas, per-path app routes — and each copy carried its own subtly different
 * index handling. It is one component because the shape never varied: map with an index, replace
 * at that index, filter it out to remove, append a blank to add.
 */
export function ListEditor<T>({
  label,
  help,
  items,
  onChange,
  blank,
  row,
  addLabel,
  emptyMessage,
  boxed,
}: ListEditorProps<T>) {
  const replace = (i: number) => (next: T) => onChange(items.map((x, j) => (j === i ? next : x)));
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i));
  return (
    <div>
      {label && <div className="text-[12px] text-fg">{label}</div>}
      {help && <div className="mb-1 text-[11.5px] leading-relaxed text-muted">{help}</div>}
      {items.length === 0 && emptyMessage && (
        <div className="py-1.5 text-[12.5px] text-muted">{emptyMessage}</div>
      )}
      {items.map((item, i) => (
        <div
          key={i}
          className={
            boxed
              ? "mb-3 rounded-lg border border-line p-3"
              : "mb-1.5 flex flex-wrap items-center gap-2"
          }
        >
          {boxed ? (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">{row(item, replace(i), i)}</div>
                <button type="button" className="text-[12px] text-crit hover:underline" onClick={() => remove(i)}>
                  remove
                </button>
              </div>
            </>
          ) : (
            <>
              {row(item, replace(i), i)}
              <button type="button" className="text-[12px] text-crit hover:underline" onClick={() => remove(i)}>
                remove
              </button>
            </>
          )}
        </div>
      ))}
      <button
        type="button"
        className="mt-1 text-[12px] text-accent hover:underline"
        onClick={() => onChange([...items, blank()])}
      >
        {addLabel}
      </button>
    </div>
  );
}

/** A bare input for use INSIDE a ListEditor row, where the label belongs to the list. */
export function RowInput({
  value,
  onChange,
  placeholder,
  mono = true,
  width = "flex-1",
  type,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  width?: string;
  type?: "text" | "password";
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      // The default fills the row with a floor so it never collapses; a caller's fixed width is that
      // width (merged, so it beats the base `w-full`) with no floor wider than itself.
      className={cn(mono ? monoInputClass : inputClass, width === "flex-1" ? "min-w-[8rem] flex-1" : [width, "min-w-0"])}
    />
  );
}

/** A titled block of fields. */
export function FieldGroup({
  title,
  help,
  children,
}: {
  title?: string;
  help?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      {title && (
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted">{title}</div>
      )}
      {help && <p className="max-w-3xl text-[12px] leading-relaxed text-muted">{help}</p>}
      {children}
    </div>
  );
}
